import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, ReactiveFormsModule } from '@angular/forms';
import { MatChipInputEvent, MatChipGrid, MatChip, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogActions } from '@angular/material/dialog';
import { ApplicationsService } from 'src/app/applications/applications.service';
import { Controller } from 'src/app/interfaces/controller';
import { QueryCheck } from 'src/app/interfaces/queryCheck';
import { ControllerModalService } from './controller-modal.service';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatFormField, MatLabel, MatInput, MatHint } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

export interface Value {
  name: string;
}

@Component({
  selector: 'mic-app-controller-modal',
  templateUrl: './controller-modal.component.html',
  styleUrls: ['./controller-modal.component.scss'],
  imports: [
    MatDialogTitle,
    MatIcon,
    MatDivider,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatChipGrid,
    MatChip,
    MatChipRemove,
    MatChipInput,
    MatHint,
    MatDialogActions,
    MatButton
  ]
})
export class ControllerModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ControllerModalComponent>>(MatDialogRef);
  data = inject<Controller>(MAT_DIALOG_DATA);
  controllerService = inject(ControllerModalService);
  private readonly applicationsService = inject(ApplicationsService);

  addOnBlur = true;
  QueryCheck: QueryCheck = {
    applicationId: this.applicationsService.myObject.id,
    sqlQuery: ''
  };
  error = '';
  resultaCheckQuery = 'NotTested';
  message = '';
  controllerForm: FormGroup;
  values: Value[] = [];
  valuesString: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  ngOnInit(): void {
    this.valuesString = this.data?.listOfValues?.split(';');
    if (this.valuesString !== undefined) {
      this.valuesString.forEach((item) => {
        this.values.push({ name: item });
      });
      this.values.pop();
    }
    this.controllerForm = new FormGroup(
      {
        name: new FormControl(this.data ? this.data.name : ''),
        regex: new FormControl(this.data ? this.data.regex : ''),
        listOfValues: new FormControl(this.valuesString !== undefined ? this.reverseConvertListOfValues(this.valuesString) : this.values),
        sqlQuery: new FormControl(this.data ? this.data.sqlQuery : '')
      },
      {
        validators: this.controlForms('name', 'regex', 'sqlQuery')
      }
    );
    this.controllerForm.get('sqlQuery').valueChanges.subscribe(() => {
      this.resultaCheckQuery = 'NotTested';
    });
  }

  private controlForms(controlName: string, controlNameA: string, controlNameC: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formGroup = control as FormGroup;
      const valueOfControle = formGroup.get(controlName)?.value;
      const valueOfControleA = formGroup.get(controlNameA)?.value;
      const valueOfControleC = formGroup.get(controlNameC)?.value;
      if (valueOfControle !== '' && (valueOfControleA !== '' || valueOfControleC !== '')) {
        return null;
      } else {
        return { valuesDoNotMatch: true };
      }
    };
  }

  /******************************************************************************************************************************👌
   * This function updates the controller if data is not null, otherwise it adds a new controller.                                *
   * @param {Controller} this.controllerForm.value The value of the connection to update or to add based on the value of the data *
   * @returns {void}                                                                                                              *
   ********************************************************************************************************************************/
  onSubmit() {
    this.controllerForm.value.listOfValues = this.convertListOfValues(this.values);
    if (this.data !== null) {
      // update an already controller
      this.controllerService.updateController(this.data.id, this.controllerForm.value).subscribe(() => {
        this.onNoClick(true);
      });
    } else {
      // Add a new Controller
      this.controllerService.addController(this.applicationsService.myObject.id, this.controllerForm.value).subscribe(() => {
        this.onNoClick(true);
      });
    }
  }

  /*****************************************************************👌
   * This function converts a list of values to a string.           *
   * @param {Value[]} listOfValues - The list of values to convert. *
   * @returns {string} - The converted string.                      *
   ******************************************************************/
  convertListOfValues(listOfValues: Value[]): string {
    let result = '';
    listOfValues.forEach((value) => {
      result += value.name + ';';
    });
    return result;
  }

  /*************************************************************************👌
   * Returns a string representation of the list of values.                  *
   * @returns {string} - The string representation of the list of values.    *
   ***************************************************************************/
  reverseConvertListOfValues(valuesString: string[]): string {
    let result = '';
    if (valuesString !== undefined) {
      valuesString.forEach((item) => {
        result = item + ',';
      });
    }
    return result.slice(0, -1);
  }
  /****************************************************👌
   * Thsi function closes th dialog with a boolean.    *
   * @param alert the boolean that send to the dialog. *
   ****************************************************/
  onNoClick(alert: boolean): void {
    this.dialogRef.close(alert);
  }

  /***********************************************👌
   * Removes an element from the list of values.  *
   * @param value - The value to remove.          *
   ************************************************/
  remove(value: { name: string }) {
    const index = this.values.indexOf(value);

    if (index >= 0) {
      this.values.splice(index, 1);
    }
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // Add our value
    if (value) {
      this.values.push({ name: value });
    }

    // Clear the input value
    event.chipInput?.clear();
    // event.chipInput!.clear();
  }

  /******************************************************************************************************👌
   * This function checks a SQL query using an HTTP request.                                              *
   * It first sets the applicationId and sqlQuery properties of the QueryCheck object.                    *
   * Then it calls the checkQuery() method of the controllerService object with the QueryCheck object     *
   *  as a parameter. If the request is successful, it sets the error property to "it's correct "         *
   *  and the resultaCheckQuery property to 'valid'.                                                      *
   * If there is an error, it sets the error property to the error message and the resultaCheckQuery      *
   *  property to 'inValid'.                                                                              *
   * Finally, it returns the value of the resultaCheckQuery property.                                     *
   * @returns {string} - The result of the query check.                                                   *
   ********************************************************************************************************/
  onCheckRequete(): string {
    this.QueryCheck.applicationId = this.applicationsService.myObject.id;
    this.QueryCheck.sqlQuery = this.controllerForm.value.sqlQuery;
    this.controllerService.checkQuery(this.QueryCheck).subscribe(
      () => {
        this.error = "it's correct ";
        this.resultaCheckQuery = 'valid';
      },
      (error) => {
        this.error = error.error.text;
        this.resultaCheckQuery = 'inValid';
      }
    );
    return this.resultaCheckQuery;
  }
}
